import { sceneObjectTypeError } from "./scene-object-types.js";
import type { SceneBehaviorCatalog, SceneObjectTypeEntry } from "./types.js";
/**
 * Validates a LayoutDocument structure before saving or rendering.
 *
 * Returns a list of human-readable error messages.
 * An empty array means the document is valid.
 */

import type {
  ComponentNode,
  ContainerNode,
  LayoutNode,
  PrimitiveNode,
} from "./types.js";

const VALID_DATA_SOURCES = new Set([
  "currentValue",
  "athlete",
  "selectedRowId",
  "selectedColumnKey",
  "modalValues",
  "inputValues",
  "currentAthlete",
  "athletes",
  "groups",
  "ageCategories",
  "weightCategories",
  "disciplines",
  "leagues",
  "levels",
  "group",
  "currentGroup",
  "currentMeet",
  "timer",
  "timerSeconds",
  "currentLift",
  "currentMouv",
  "attemptNumber",
  "placementIfSuccessful",
  "meetName",
  "startDate",
  "endDate",
  "refereeLights",
  "nextLiftData",
  "records",
  "niveaux",
  "organisation",
  "organisations",
  "themeColors",
  "displaySettings",
  "attemptTimers",
  "attemptTimerSeconds",
  "currentMovement",
]);

const VALID_PRIMITIVES = new Set(["text", "image", "video", "shape"]);
const VALID_COMPONENTS = new Set([
  "ranking",
  "barLoader",
  "layoutList",
  "qrCode",
  "icon",
  "customDisciplineInput",
  "customDisciplineButton",
]);
const VALID_SHAPE_TYPES = new Set([
  "rectangle",
  "square",
  "ellipse",
  "oval",
  "line",
  "triangle",
  "rightTriangle",
  "trapezoid",
  "parallelogram",
  "pentagon",
  "hexagon",
  "octagon",
  "star",
]);
const VALID_FORMATS = new Set(["date", "time", "uppercase", "lowercase", "kg"]);
const VALID_OPERATORS = new Set([
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "defined",
  "empty",
]);
const BINARY_OPERATORS = new Set(["eq", "neq", "gt", "gte", "lt", "lte"]);
const STYLE_VALUE_KIND_BY_KEY: Record<
  string,
  "boolean" | "number" | "string" | "numberOrString"
> = {
  visibility: "boolean",
  width: "number",
  minWidth: "numberOrString",
  height: "number",
  margin: "numberOrString",
  marginTop: "numberOrString",
  marginRight: "numberOrString",
  marginBottom: "numberOrString",
  marginLeft: "numberOrString",
  backgroundColor: "string",
  color: "string",
  fontSize: "number",
  lineHeight: "numberOrString",
  letterSpacing: "numberOrString",
  fontFamily: "string",
  fontWeight: "string",
  textAlign: "string",
  textTransform: "string",
  whiteSpace: "string",
  border: "string",
  borderTop: "string",
  borderRight: "string",
  borderBottom: "string",
  borderLeft: "string",
  borderRadius: "numberOrString",
  borderTopLeftRadius: "string",
  borderTopRightRadius: "string",
  borderBottomRightRadius: "string",
  borderBottomLeftRadius: "string",
  opacity: "number",
  padding: "numberOrString",
  paddingTop: "numberOrString",
  paddingRight: "numberOrString",
  paddingBottom: "numberOrString",
  paddingLeft: "numberOrString",
  transform: "string",
  backgroundImage: "string",
  position: "string",
  overflow: "string",
  breakBefore: "string",
  breakInside: "string",
  filter: "string",
  cursor: "string",
  boxShadow: "string",
  textShadow: "string",
  textDecorationLine: "string",
  textDecorationColor: "string",
  textDecorationStyle: "string",
  textDecorationThickness: "number",
};

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Returns whether a condition expression is valid (for use in the editor modal).
 */
export function isConditionExprValid(condition: unknown): boolean {
  if (!condition || typeof condition !== "object") return false;
  const errors: string[] = [];
  validateConditionExpr(
    condition as Record<string, unknown>,
    errors,
    "condition",
  );
  return errors.length === 0;
}

export function addNodeDefaultValues(node: LayoutNode) {
  const style = node.style ?? {};
  // Do not use truthiness: visibility === false is valid and must be preserved.
  if (typeof style.visibility !== "boolean") {
    style.visibility = true;
  }
  node.style = style;
  return node;
}

/**
 * Validates a LayoutDocument.
 *
 * @param doc - The document to validate.
 * @returns Validation result with a list of error messages.
 */
export function validateLayout(doc: unknown): ValidationResult {
  const errors: string[] = [];

  if (!doc || typeof doc !== "object") {
    return { valid: false, errors: ["Document must be a non-null object."] };
  }

  const d = doc as Record<string, unknown>;

  if (typeof d.version !== "number" || d.version < 1) {
    errors.push("'version' must be a positive number.");
  }

  validateMeta(d.meta, errors);
  validateScene(d.scene, errors);

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

function validateMeta(meta: unknown, errors: string[]): void {
  if (!meta || typeof meta !== "object") {
    errors.push("'meta' is required and must be an object.");
    return;
  }

  const m = meta as Record<string, unknown>;

  if (typeof m.id !== "string" || !m.id) {
    errors.push("'meta.id' is required.");
  }
  if (typeof m.name !== "string" || !m.name) {
    errors.push("'meta.name' is required.");
  }
  if (m.type !== "widget" && m.type !== "display") {
    errors.push("'meta.type' must be 'widget' or 'display'.");
  }

  const canvas = m.canvas as Record<string, unknown> | undefined;
  if (!canvas || typeof canvas !== "object") {
    errors.push("'meta.canvas' is required.");
  } else {
    if (typeof canvas.width !== "number" || canvas.width <= 0) {
      errors.push("'meta.canvas.width' must be a positive number.");
    }
    if (typeof canvas.height !== "number" || canvas.height <= 0) {
      errors.push("'meta.canvas.height' must be a positive number.");
    }
  }
}

// ---------------------------------------------------------------------------
// Scene
// ---------------------------------------------------------------------------

function validateScene(scene: unknown, errors: string[]): void {
  if (!scene || typeof scene !== "object") {
    errors.push("'scene' is required and must be an object.");
    return;
  }

  const s = scene as Record<string, unknown>;
  if (!Array.isArray(s.children)) {
    errors.push("'scene.children' must be an array.");
    return;
  }

  const ids = new Set<string>();
  for (const child of s.children as unknown[]) {
    validateNode(child, errors, ids, "scene");
  }

  if (s.catalog != null) {
    validateCatalog(s.catalog, errors);
  }

  if (s.nodeNameCounters != null) {
    if (
      typeof s.nodeNameCounters !== "object" ||
      Array.isArray(s.nodeNameCounters)
    ) {
      errors.push("'scene.nodeNameCounters' must be an object when present.");
    } else {
      for (const [key, value] of Object.entries(
        s.nodeNameCounters as Record<string, unknown>,
      )) {
        if (typeof key !== "string" || key.trim() === "") {
          errors.push(
            "'scene.nodeNameCounters' keys must be non-empty strings.",
          );
          break;
        }
        if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
          errors.push(
            `'scene.nodeNameCounters["${key}"]' must be a non-negative number.`,
          );
          break;
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

function validateCatalog(catalog: unknown, errors: string[]): void {
  if (typeof catalog !== "object" || catalog === null) {
    errors.push("'scene.catalog' must be an object when present.");
    return;
  }

  const c = catalog as Record<string, unknown>;

  if (c.animations != null) {
    if (!Array.isArray(c.animations)) {
      errors.push("'scene.catalog.animations' must be an array.");
    } else {
      const namesSeen = new Set<string>();
      for (const entry of c.animations as Record<string, unknown>[]) {
        if (!entry || typeof entry !== "object") {
          errors.push("Each animation entry must be an object.");
          continue;
        }
        if (typeof entry.id !== "string" || !entry.id) {
          errors.push("Animation entry missing 'id'.");
        }
        if (typeof entry.name !== "string" || !entry.name) {
          errors.push("Animation entry missing 'name'.");
        } else {
          const lower = (entry.name as string).trim().toLocaleLowerCase();
          if (namesSeen.has(lower)) {
            errors.push(
              `Duplicate animation name '${entry.name}' (case-insensitive).`,
            );
          }
          namesSeen.add(lower);
        }
      }
    }
  }

  if (c.customEvents != null) {
    if (!Array.isArray(c.customEvents)) {
      errors.push("'scene.catalog.customEvents' must be an array.");
    } else {
      const namesSeen = new Set<string>();
      for (const entry of c.customEvents as Record<string, unknown>[]) {
        if (!entry || typeof entry !== "object") {
          errors.push("Each custom event entry must be an object.");
          continue;
        }
        if (typeof entry.id !== "string" || !entry.id) {
          errors.push("Custom event entry missing 'id'.");
        }
        if (typeof entry.name !== "string" || !entry.name) {
          errors.push("Custom event entry missing 'name'.");
        } else {
          const lower = (entry.name as string).trim().toLocaleLowerCase();
          if (namesSeen.has(lower)) {
            errors.push(
              `Duplicate custom event name '${entry.name}' (case-insensitive).`,
            );
          }
          namesSeen.add(lower);
        }
        const tg = entry.triggerGraph as Record<string, unknown> | undefined;
        if (tg && typeof tg === "object" && Array.isArray(tg.nodes)) {
          const hasEventOutput = (tg.nodes as Record<string, unknown>[]).some(
            (n) => n.type === "control" && n.controlType === "eventOutput",
          );
          if (!hasEventOutput) {
            errors.push(
              `Custom event '${entry.name ?? entry.id}' triggerGraph must contain an eventOutput node.`,
            );
          }
        }
      }
    }
  }

  if (c.functions != null) {
    if (!Array.isArray(c.functions)) {
      errors.push("'scene.catalog.functions' must be an array.");
    } else {
      const namesSeen = new Set<string>();
      for (const entry of c.functions as Record<string, unknown>[]) {
        if (!entry || typeof entry !== "object") {
          errors.push("Each function entry must be an object.");
          continue;
        }
        if (typeof entry.id !== "string" || !entry.id) {
          errors.push("Function entry missing 'id'.");
        }
        if (typeof entry.name !== "string" || !entry.name) {
          errors.push("Function entry missing 'name'.");
        } else {
          const lower = (entry.name as string).trim().toLocaleLowerCase();
          if (namesSeen.has(lower)) {
            errors.push(
              `Duplicate function name '${entry.name}' (case-insensitive).`,
            );
          }
          namesSeen.add(lower);
        }
        const graph = entry.graph as Record<string, unknown> | undefined;
        if (graph && typeof graph === "object" && Array.isArray(graph.nodes)) {
          const nodes = graph.nodes as Record<string, unknown>[];
          const beginCount = nodes.filter(
            (node) =>
              node.type === "control" && node.controlType === "functionBegin",
          ).length;
          const endCount = nodes.filter(
            (node) =>
              node.type === "control" && node.controlType === "functionEnd",
          ).length;
          if (beginCount !== 1) {
            errors.push(
              `Function '${entry.name ?? entry.id}' graph must contain exactly one functionBegin node.`,
            );
          }
          if (endCount !== 1) {
            errors.push(
              `Function '${entry.name ?? entry.id}' graph must contain exactly one functionEnd node.`,
            );
          }
        }
      }
    }
  }

  if (c.variables != null) {
    if (!Array.isArray(c.variables)) {
      errors.push("'scene.catalog.variables' must be an array.");
    } else {
      const namesSeen = new Set<string>();
      for (const entry of c.variables as Record<string, unknown>[]) {
        if (!entry || typeof entry !== "object") {
          errors.push("Each variable entry must be an object.");
          continue;
        }
        if (typeof entry.id !== "string" || !entry.id) {
          errors.push("Variable entry missing 'id'.");
        }
        if (typeof entry.name !== "string" || !entry.name) {
          errors.push("Variable entry missing 'name'.");
        } else {
          const lower = (entry.name as string).trim().toLocaleLowerCase();
          if (namesSeen.has(lower)) {
            errors.push(
              `Duplicate variable name '${entry.name}' (case-insensitive).`,
            );
          }
          namesSeen.add(lower);
        }
        if (
          typeof entry.outputValueType !== "string" ||
          !entry.outputValueType
        ) {
          errors.push(
            `Variable '${entry.name ?? entry.id}' missing 'outputValueType'.`,
          );
        }
        const graph = entry.graph as Record<string, unknown> | undefined;
        if (graph && typeof graph === "object" && Array.isArray(graph.nodes)) {
          const nodes = graph.nodes as Record<string, unknown>[];
          const beginCount = nodes.filter(
            (node) =>
              node.type === "control" && node.controlType === "variableBegin",
          ).length;
          const endCount = nodes.filter(
            (node) =>
              node.type === "control" && node.controlType === "variableEnd",
          ).length;
          if (beginCount > 1) {
            errors.push(
              `Variable '${entry.name ?? entry.id}' graph must contain at most one variableBegin node.`,
            );
          }
          if (endCount !== 1) {
            errors.push(
              `Variable '${entry.name ?? entry.id}' graph must contain exactly one variableEnd node.`,
            );
          }
        }
      }
    }
  }
  if (c.objectTypes != null) {
    if (!Array.isArray(c.objectTypes))
      errors.push("'scene.catalog.objectTypes' must be an array.");
    else {
      const names = new Set<string>(),
        ids = new Set<string>();
      const valid = c.objectTypes.filter(
        (raw: any) =>
          raw &&
          typeof raw === "object" &&
          typeof raw.id === "string" &&
          typeof raw.name === "string" &&
          (raw.baseType == null || typeof raw.baseType === "string") &&
          (raw.extensions == null ||
            (Array.isArray(raw.extensions) &&
              raw.extensions.every(
                (e: any) =>
                  e && typeof e.id === "string" && typeof e.typeId === "string",
              ))) &&
          Array.isArray(raw.fields) &&
          raw.fields.every(
            (f: any) =>
              f &&
              typeof f === "object" &&
              typeof f.key === "string" &&
              (f.objectTypeId == null || typeof f.objectTypeId === "string"),
          ),
      );
      if (valid.length !== c.objectTypes.length)
        errors.push("Invalid scene object type.");
      for (const raw of valid) {
        const type = raw as SceneObjectTypeEntry,
          name = type.name.trim().toLocaleLowerCase();
        if (names.has(name) || ids.has(type.id))
          errors.push("Duplicate scene object type.");
        names.add(name);
        ids.add(type.id);
        const error = sceneObjectTypeError(type, {
          ...c,
          objectTypes: valid,
        } as unknown as SceneBehaviorCatalog);
        if (error) errors.push("Object type '" + type.name + "': " + error);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Node
// ---------------------------------------------------------------------------

function validateNode(
  node: unknown,
  errors: string[],
  ids: Set<string>,
  parentPath: string,
): void {
  if (!node || typeof node !== "object") {
    errors.push(`${parentPath}: node must be a non-null object.`);
    return;
  }

  const n = addNodeDefaultValues(node as LayoutNode);
  const path = `${parentPath}/${n.id ?? "unknown"}`;

  if (typeof n.id !== "string" || !n.id) {
    errors.push(`${path}: 'id' is required.`);
  } else if (ids.has(n.id)) {
    errors.push(`${path}: duplicate node id '${n.id}'.`);
  } else {
    ids.add(n.id);
  }
  if (typeof n.x !== "number") errors.push(`${path}: 'x' must be a number.`);
  if (typeof n.y !== "number") errors.push(`${path}: 'y' must be a number.`);
  if (typeof n.width !== "number" || n.width < 0)
    errors.push(`${path}: 'width' must be a non-negative number.`);
  if (typeof n.height !== "number" || n.height < 0)
    errors.push(`${path}: 'height' must be a non-negative number.`);
  if (n.rotation != null && typeof n.rotation !== "number")
    errors.push(`${path}: 'rotation' must be a number when provided.`);
  if (typeof n.zIndex !== "number")
    errors.push(`${path}: 'zIndex' must be a number.`);

  const style = n.style as Record<string, unknown> | undefined;
  if (style != null && typeof style === "object") {
    validateStyle(style, errors, path);
  }

  switch (n.kind) {
    case "primitive":
      validatePrimitive(n as unknown as PrimitiveNode, errors, path);
      break;
    case "container":
      validateContainer(n as unknown as ContainerNode, errors, ids, path);
      break;
    case "component":
      validateComponent(n as unknown as ComponentNode, errors, path);
      break;
    default:
      errors.push(
        `${path}: 'kind' must be 'primitive', 'container' or 'component'.`,
      );
  }
}

// ---------------------------------------------------------------------------
// Primitive
// ---------------------------------------------------------------------------

function validatePrimitive(
  node: PrimitiveNode,
  errors: string[],
  path: string,
): void {
  if (!VALID_PRIMITIVES.has(node.primitive)) {
    errors.push(
      `${path}: 'primitive' must be one of: ${[...VALID_PRIMITIVES].join(", ")}.`,
    );
  }

  if (
    node.primitive === "shape" &&
    node.shapeType &&
    !VALID_SHAPE_TYPES.has(node.shapeType)
  ) {
    errors.push(
      `${path}: 'shapeType' must be one of: ${[...VALID_SHAPE_TYPES].join(", ")}.`,
    );
  }

  if (node.text != null) {
    if (typeof node.text !== "string") {
      errors.push(`${path}: 'text' must be a string.`);
    }
  }
}

function validateStyle(
  style: Record<string, unknown>,
  errors: string[],
  path: string,
): void {
  for (const [key, value] of Object.entries(style)) {
    if (value == null) continue;
    const propPath = `${path}.style.${key}`;
    const expectedKind = STYLE_VALUE_KIND_BY_KEY[key];

    if (!expectedKind) {
      errors.push(`${propPath}: style key '${key}' is not supported.`);
      continue;
    }

    if ((key === "height" || key === "width") && value === "auto") continue;

    if (expectedKind === "number" && typeof value !== "number") {
      errors.push(`${propPath}: expected a number value.`);
      continue;
    }
    if (expectedKind === "boolean" && typeof value !== "boolean") {
      errors.push(`${propPath}: expected a boolean value.`);
      continue;
    }
    if (expectedKind === "string" && typeof value !== "string") {
      errors.push(`${propPath}: expected a string value.`);
      continue;
    }
    if (
      expectedKind === "numberOrString" &&
      typeof value !== "number" &&
      typeof value !== "string"
    ) {
      errors.push(`${propPath}: expected a number or string value.`);
    }
  }
}

function validateParts(parts: unknown, errors: string[], path: string): void {
  if (!Array.isArray(parts)) {
    errors.push(`${path}: 'parts' must be an array.`);
    return;
  }
  parts.forEach((part, index) => {
    const partPath = `${path}.parts[${index}]`;
    if (!part || typeof part !== "object") {
      errors.push(`${partPath}: must be an object.`);
      return;
    }
    const p = part as Record<string, unknown>;
    if (p.type === "literal") {
      if (typeof p.value !== "string") {
        errors.push(`${partPath}: literal part must have a string 'value'.`);
      }
      return;
    }
    if (p.type === "binding") {
      if (!VALID_DATA_SOURCES.has(p.source as string)) {
        errors.push(
          `${partPath}: binding.source '${p.source}' is not a valid data source.`,
        );
      }
      if (p.format != null && !VALID_FORMATS.has(p.format as string)) {
        errors.push(`${partPath}: binding.format '${p.format}' is not valid.`);
      }
      return;
    }
    if (p.type === "conditional") {
      validateConditionalPart(p as Record<string, unknown>, errors, partPath);
      return;
    }
    errors.push(
      `${partPath}: part 'type' must be 'literal', 'binding' or 'conditional'.`,
    );
  });
}

function validateConditionOperand(
  operand: unknown,
  errors: string[],
  path: string,
): void {
  if (!operand || typeof operand !== "object") {
    errors.push(`${path}: operand must be an object.`);
    return;
  }
  const o = operand as Record<string, unknown>;
  if (o.type === "literal") {
    return;
  }
  if (o.type === "binding") {
    if (!VALID_DATA_SOURCES.has(o.source as string)) {
      errors.push(
        `${path}: binding.source '${o.source}' is not a valid data source.`,
      );
    }
    return;
  }
  errors.push(`${path}: operand type must be 'literal' or 'binding'.`);
}

function validateConditionExpr(
  condition: Record<string, unknown>,
  errors: string[],
  path: string,
): void {
  if (condition.type === "and") {
    const conditions = condition.conditions as unknown[] | undefined;
    if (!Array.isArray(conditions) || conditions.length === 0) {
      errors.push(
        `${path}: 'and' group must have a non-empty 'conditions' array.`,
      );
    } else {
      conditions.forEach((c, i) => {
        validateConditionExpr(
          c as Record<string, unknown>,
          errors,
          `${path}.conditions[${i}]`,
        );
      });
    }
    return;
  }
  if (condition.type === "or") {
    const conditions = condition.conditions as unknown[] | undefined;
    if (!Array.isArray(conditions) || conditions.length === 0) {
      errors.push(
        `${path}: 'or' group must have a non-empty 'conditions' array.`,
      );
    } else {
      conditions.forEach((c, i) => {
        validateConditionExpr(
          c as Record<string, unknown>,
          errors,
          `${path}.conditions[${i}]`,
        );
      });
    }
    return;
  }
  if (condition.left == null) {
    errors.push(`${path}: simple condition must have 'left' operand.`);
  } else {
    validateConditionOperand(condition.left, errors, `${path}.left`);
  }
  if (!VALID_OPERATORS.has(condition.op as string)) {
    errors.push(`${path}: operator '${condition.op}' is not valid.`);
  }
  const op = condition.op as string;
  if (BINARY_OPERATORS.has(op)) {
    if (condition.right == null) {
      errors.push(`${path}: binary operator '${op}' requires 'right' operand.`);
    } else {
      validateConditionOperand(condition.right, errors, `${path}.right`);
    }
  }
}

function validateConditionalPart(
  part: Record<string, unknown>,
  errors: string[],
  path: string,
): void {
  const rules = part.rules as unknown[] | undefined;
  if (!Array.isArray(rules) || rules.length === 0) {
    errors.push(
      `${path}: conditional part must have a non-empty 'rules' array.`,
    );
  } else {
    rules.forEach((rule, i) => {
      const rulePath = `${path}.rules[${i}]`;
      const r = rule as Record<string, unknown>;
      if (r.condition != null) {
        validateConditionExpr(
          r.condition as Record<string, unknown>,
          errors,
          `${rulePath}.condition`,
        );
      } else {
        errors.push(`${rulePath}: 'condition' is required.`);
      }
      if (Array.isArray(r.parts)) {
        validateParts(r.parts, errors, rulePath);
      } else {
        errors.push(`${rulePath}: 'parts' must be an array.`);
      }
    });
  }
  const fallback = part.fallback;
  if (!Array.isArray(fallback)) {
    errors.push(`${path}: 'fallback' must be an array of parts.`);
  } else {
    validateParts(fallback, errors, `${path}.fallback`);
  }
}

// ---------------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------------

function validateContainer(
  node: ContainerNode,
  errors: string[],
  ids: Set<string>,
  path: string,
): void {
  if (node.layout !== "flex" && node.layout !== "absolute") {
    errors.push(`${path}: container 'layout' must be 'flex' or 'absolute'.`);
  }

  if (!Array.isArray(node.children)) {
    errors.push(`${path}: container 'children' must be an array.`);
    return;
  }

  for (const child of node.children) {
    validateNode(child, errors, ids, path);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function validateComponent(
  node: ComponentNode,
  errors: string[],
  path: string,
): void {
  if (!VALID_COMPONENTS.has(node.component)) {
    errors.push(`${path}: component '${node.component}' is not registered.`);
  }

  if (node.dataBinding) {
    if (!VALID_DATA_SOURCES.has(node.dataBinding.path.split(".")[0] ?? "")) {
      errors.push(
        `${path}: dataBinding.source '${node.dataBinding.path}' is not a valid data source.`,
      );
    }
  }
}
